/**
 * Lead Observability Service
 * 
 * Tracks lead request events for monitoring tour/contact/info form submissions.
 * Integrates with NewRelic Browser APM for real-time alerting.
 */

import { trackEvent } from '../utils/event-tracker';
import { LEAD_REQUEST_FAILURE } from '../constants/lead-events';

/**
 * Error category for lead request failures.
 * - missing_fields: client-side, required fields not entered
 * - invalid_data: server-side validation (fields entered but invalid)
 * - consent_not_given: required disclaimers not checked
 */
export type LeadErrorCategory =
  | 'missing_fields'
  | 'invalid_data'
  | 'consent_not_given'
  | 'network_error'
  | 'api_error'
  | 'unknown_error';

/**
 * PVR data passed to failure events (will be stringified)
 */
export interface PvrData {
  leadType: string;
  listingId?: string;
  email?: string;
  isAuthenticated: boolean;
}

/**
 * Parameters for tracking a failed lead request.
 */
export interface LeadRequestFailureParams {
  /** Lead/form context data */
  pvrData: PvrData;

  /** Category of the error */
  errorCategory: LeadErrorCategory;

  /** Error message (matches what is shown in the UI) */
  errorMessage: string;
}

/**
 * Lead Observability Service
 * 
 * Pure service with no React dependencies.
 * Called from React components/hooks for tracking.
 */
export const LeadObservabilityService = {
  /**
   * Track failed lead request (when lead submission fails)
   */
  trackRequestFailure(params: LeadRequestFailureParams): void {
    const { pvrData, errorCategory, errorMessage } = params;

    trackEvent(LEAD_REQUEST_FAILURE, {
      pvrData: JSON.stringify(pvrData),
      errorCategory,
      errorMessage,
    });
  },
} as const;
